/* Created by Interactive 63 - https://interactive63.com */
/**
 * Editor actions for curved motion paths: the handles on position keyframes, the straight,
 * smooth or corner shape of the path through a keyframe, and orient along path. Each is one undo
 * step, and handle drags coalesce into one.
 */
import type { Keyframe, MotionHandles, Vec2 } from '../../core/model/types';
import { findKeyframe } from '../../core/timeline/keyframes';
import { mirroredHandle, smoothHandles } from '../../core/timeline/motion-curve';
import type { Store } from './store';

export type PathMode = 'straight' | 'smooth' | 'corner';

/** How the path passes through a keyframe. */
export function pathModeOf(keyframe: Keyframe): PathMode {
  if (!keyframe.motion?.in && !keyframe.motion?.out) return 'straight';
  return keyframe.motion.corner ? 'corner' : 'smooth';
}

/** Replaces a keyframe's handles; undefined straightens the path through it. */
export function setMotionHandles(
  store: Store,
  keyframeId: string,
  motion: MotionHandles | undefined,
  coalesceKey?: string,
): void {
  store.updateProject(
    'Edit motion path',
    (draft) => {
      const found = findKeyframe(draft, keyframeId);
      if (!found) return;
      if (motion && (motion.in || motion.out)) found.keyframe.motion = motion;
      else delete found.keyframe.motion;
    },
    coalesceKey ? { coalesceKey } : undefined,
  );
}

/**
 * Moves one handle of a keyframe. On a smooth keyframe the other handle turns to stay in line,
 * keeping its length; `breakCorner` makes the keyframe a corner so the handles move apart.
 */
export function moveMotionHandle(
  store: Store,
  keyframeId: string,
  side: 'in' | 'out',
  handle: Vec2,
  breakCorner: boolean,
  coalesceKey: string,
): void {
  const found = findKeyframe(store.getState().project, keyframeId);
  if (!found) return;
  const current = found.keyframe.motion ?? {};
  const corner = breakCorner || current.corner === true;
  const other = side === 'in' ? 'out' : 'in';
  const opposite = corner ? current[other] : mirroredHandle(handle, current[other]);
  setMotionHandles(
    store,
    keyframeId,
    {
      [side]: handle,
      ...(opposite ? { [other]: opposite } : {}),
      ...(corner ? { corner: true } : {}),
    },
    coalesceKey,
  );
}

/** Sets how the path passes through a keyframe, adding smooth handles where it had none. */
export function setPathMode(store: Store, keyframeId: string, mode: PathMode): void {
  const found = findKeyframe(store.getState().project, keyframeId);
  if (!found) return;
  if (mode === 'straight') {
    setMotionHandles(store, keyframeId, undefined);
    return;
  }
  const existing = found.keyframe.motion;
  const handles =
    existing && (existing.in || existing.out)
      ? { ...existing }
      : smoothHandles(found.track.keyframes, found.index);
  if (mode === 'smooth' && handles.out && handles.in) {
    handles.in = mirroredHandle(handles.out, handles.in);
  }
  const shape: MotionHandles = {
    ...(handles.in ? { in: handles.in } : {}),
    ...(handles.out ? { out: handles.out } : {}),
  };
  setMotionHandles(store, keyframeId, mode === 'corner' ? { ...shape, corner: true } : shape);
}

/** Straightens a curved keyframe, or gives a straight one smooth handles. */
export function toggleSmooth(store: Store, keyframeId: string): void {
  const found = findKeyframe(store.getState().project, keyframeId);
  if (!found) return;
  setPathMode(store, keyframeId, pathModeOf(found.keyframe) === 'straight' ? 'smooth' : 'straight');
}

/** Turns orient along path on or off for a position track. */
export function setTrackOrient(store: Store, trackId: string, orient: boolean): void {
  store.updateProject(orient ? 'Orient along path' : 'Stop orienting along path', (draft) => {
    const track = draft.tracks.find((t) => t.id === trackId);
    if (!track || track.property !== 'translate') return;
    if (orient) track.orient = true;
    else delete track.orient;
  });
}
